import { useLayoutEffect, useRef } from 'react';

/**
 * Returns the first record to the top of its scroll region whenever the query
 * behind the records changes.
 *
 * A page step, a new search term or a different filter produces a different set
 * of records, and inheriting the previous offset drops the operator into the
 * middle of them. `key` therefore identifies the query — a plain refresh leaves
 * it alone, so acting on a row does not cost the operator their place.
 *
 * Applied before paint: the new records must never be seen at the old offset.
 * Only the vertical offset is reset; a column an operator scrolled to sideways
 * is still the column they are reading.
 */
export function useScrollReset<T extends HTMLElement>(key: string | number | undefined) {
  const ref = useRef<T | null>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    // The ref may be on the scroll container itself (a DataTable's body) or on
    // a child of one (a document page's list, whose scroll lives on the shell).
    // Walk up from the element to the first ancestor that is actually scrolled.
    let target: HTMLElement | null = node;
    while (target && target.scrollTop === 0) {
      target = target.parentElement;
    }
    if (target) {
      target.scrollTop = 0;
    }
  }, [key]);

  return ref;
}
